import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarIcon, 
  ArrowRightIcon
} from '@heroicons/react/24/outline';
// import newsData from '../constants/news.json';

interface NewsArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  category: string;
  featured: boolean;
  readTime: string;
  filePath: string;
  innerImage: string;
}

export default function News() {
  // State for articles and loading
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch articles from /data/news.json
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data/news.json');
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (data.articles && Array.isArray(data.articles)) {
          setArticles(data.articles);
          setError(null);
        } else {
          throw new Error('Invalid data format: expected articles array');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('Failed to fetch news articles:', errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <div className="bg-white page-transition">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
              Latest News
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100">
              Stay updated with the latest happenings at Bethanchok Homestay, upcoming events, 
              and insights into Nepali culture and traditions.
            </p>
          </div>
        </div>
      </div>      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="mx-auto w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
            <p className="text-neutral-500 text-lg">Loading news articles...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <CalendarIcon className="h-8 w-8 text-red-400" />
            </div>
            <p className="text-red-500 text-lg">Error loading news articles</p>
            <p className="text-red-400 text-sm mt-2">{error}</p>
          </div>
        )}

        {/* Articles Grid */}
        {!loading && !error && (
          <>
            {articles.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">No articles found</h3>
                <p className="text-neutral-600">
                  Check back later for the latest news and updates.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/news/${article.slug}`}
                    className="group relative bg-white rounded-2xl shadow-soft overflow-hidden hover:shadow-soft-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                  >
                    <article>
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between text-sm text-neutral-500 mb-3">
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDate(article.date)}
                          </div>
                          <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs font-medium">
                            {article.category}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-neutral-900 mb-3 group-hover:text-primary-600 transition-colors duration-200 leading-tight">
                          {article.title}
                        </h3>
                        <p className="text-neutral-600 mb-4 text-sm leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center text-primary-600 font-semibold text-sm group">
                            Read More
                            <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                          </span>
                          <span className="text-xs text-neutral-400">{article.readTime}</span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
